import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * A labelled group of fields inside a form sheet.
 *
 * Every create form used `text-lg font-semibold` headings with `gap-10`
 * between groups, which cost roughly 40px of height per boundary and pushed
 * Save below the fold on almost every one. The label here is a quiet caption,
 * not a heading: the form's own title already says what is being created, and
 * these only need to separate one group from the next.
 *
 * `columns` is what stops fields stacking. Two short inputs on one row is the
 * difference between a form that fits and a form that scrolls.
 */
export function FormSection({
  label,
  columns = 2,
  pairFrom = "sm",
  className,
  children,
}: {
  label?: string;
  columns?: 1 | 2 | 3;
  /**
   * "sm" pairs fields from 640px up; "always" pairs them on a phone too.
   * Two short inputs -- first and last name, title and company -- fit side by
   * side even in portrait, and pairing them is what keeps the form off the
   * scroll.
   */
  pairFrom?: "sm" | "always";
  className?: string;
  children: ReactNode;
}) {
  const prefix = pairFrom === "always" ? "" : "sm:";
  const cols =
    columns === 3
      ? `${prefix}grid-cols-3`
      : columns === 2
        ? `${prefix}grid-cols-2`
        : `${prefix}grid-cols-1`;

  return (
    <section className={cn("space-y-2", className)}>
      {label ? (
        <h6 className="text-[0.7rem] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </h6>
      ) : null}
      <div
        className={cn(
          "grid gap-x-3 gap-y-3",
          pairFrom === "always" ? "grid-cols-2" : "grid-cols-1",
          cols,
          "[&>*]:min-w-0",
        )}
      >
        {children}
      </div>
    </section>
  );
}

/** Opts a single field out of the grid and across the full row. */
export function FormRow({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <div className={cn("sm:col-span-full", className)}>{children}</div>;
}
